import { useEffect, useState } from 'react';
import cn from 'classnames';
import getConfig from 'next/config';
import { Card, Clickable } from '@fabric-ds/react';

// TODO use dynamic import to decrease size of first bundle to load
// TODO also try to only call this method once for the whole page
import createHeartComponent from '@finn-no/favorite-heart-component';

import { resultItemPriceString, resultItemLocationString } from '@/utils/formatting';
import { trackClick } from '@/lib/tracking/tracking';
import { trackFusClick } from '@/api/api';

import type { Filters as FiltersType, ResultItem as ResultItemType } from '@/domain';

import styles from './ResultItem.module.css';

export type ResultItemProps = {
    result: ResultItemType;
    index: number;
    loginId?: number;
    filters: FiltersType;
    imagePreloaded?: boolean;
    isMobile?: boolean;
};

const {
    publicRuntimeConfig: { contextPath, favouriteBaseUri },
} = getConfig();

let Heart: ReturnType<typeof createHeartComponent> | undefined;

const makeSrcSet = (path) =>
    path?.replace ? `${path.replace('/default/', '/640x480c/')} 640w, ` + `${path.replace('/default/', '/320x240c/')} 320w` : path;
const applyImageRule = (url, rule) => (url?.replace ? url.replace('/default/', rule) : url);

const CreditCard = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16">
        <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.356 7.625 14.7 1.66a1 1 0 0 0-1.104-.885L2.994 1.943a1 1 0 0 0-.885 1.104l.126 1.142"
        />
        <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12.359 6H1.64a.98.98 0 0 0-.974.982v7.369c0 .542.436.982.974.982h10.718c.538 0 .974-.44.974-.982V6.982A.98.98 0 0 0 12.36 6ZM.667 12.666h12.666m-10-4h1.333m.276-4.254 1.49-.165"
        />
    </svg>
);

export const ResultItem = (props: ResultItemProps) => {
    const [showModal, setShowModal] = useState(false);
    const searchDates = props.filters?.booking_from && props.filters?.booking_to;
    const isTransactionalAd = props.result.ad_type === 'private' && props.result.country === 'Norge';

    useEffect(() => {
        if (props.loginId) {
            Heart = createHeartComponent({
                userId: props.loginId,
                redirectUrl: props.loginId ? '' : window.location.origin + window.location.pathname,
                baseUri: `${favouriteBaseUri.slice(0, -1)}`,
            });
        }

        return () => {
            Heart = undefined;
        };
    }, []);

    const handleFavouriteClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setShowModal(true);
    };

    const selectItem = () => {
        if (sessionStorage) {
            sessionStorage.setItem('searchParams', window.location.search);
        }

        // A click on a FUS ad must be tracked
        if (props.result.upsell) {
            trackFusClick(props.result.id)

                .then((result) => console.log(result)) // TODO remove this logging

                .catch((reason) => console.error(`FUS click tracking failed: ${reason}`));
        }

        // Only do this when running in Cypress, i.e. this code will be removed from the production build
        if (process.env.NODE_ENV === 'development') {
            // To help out with verifying that a network call for tracking FUS is not done on
            // a non-FUS ad, set `__E2E_TEST.fusClicked` true/false on the `window` object
            // so it can be checked in the Cypress test
            // @ts-ignore __E2E_TEST does not exist on window
            const E2E_TEST = window.__E2E_TEST || {};
            E2E_TEST.fusClicked = !!props.result.upsell;
            // @ts-ignore __E2E_TEST does not exist on window
            window.__E2E_TEST = E2E_TEST;
        }

        trackClick({
            type: 'Click',
            intent: 'Open',
            name: 'Search result clicked',
            object: {
                id: props.result.id,
                type: 'ListingItem',
                name: 'Search result',
                rank: props.index + 1, // index is zero-based
                isTopPremiumPosition: props.result.upsell === true,
                paidPromotionType: props.result.upsell === true ? 'fus' : undefined,
            },
            target: {
                id: props.result.id,
                type: 'ClassifiedAd',
                name: props.result.heading,
                contentId: props.result.id,
                category: 'travel > holiday rental',
            },
            vertical: {
                name: 'travel',
                subVertical: 'holiday_rental',
            },
        });
    };

    const showTrasactionBadge = () =>
        isTransactionalAd ? (
            <p
                className={cn(
                    styles.upsellAd,
                    'absolute top-0 left-0 bg-aqua-50 py-4 rounded-br px-8 text-12 flex flex-row gap-4 whitespace-nowrap',
                )}
                style={{ borderRadius: '0 0 4px 0' }}
                data-testid="trasactionItem">
                <CreditCard />
                Enkel bestilling
            </p>
        ) : (
            <></>
        );

    const showUpsellAds = () =>
        props.result.upsell ? (
            <p
                className={cn(styles.upsellAd, 'bg-aqua-200 p-4 mb-12 rounded-4 text-12 inline-block whitespace-nowrap')}
                data-testid="upsellItem">
                Betalt plassering
            </p>
        ) : (
            <></>
        );

    const itemUrl = searchDates
        ? `${contextPath}/ad.html?finnkode=${props.result.id}&fra=${props.filters?.booking_from}&til=${props.filters?.booking_to}`
        : `${contextPath}/ad.html?finnkode=${props.result.id}`;

    return (
        <Card
            className={styles.resultItem}
            key={`resultItem${props.index}`}
            data-item-finnid={props.result.id}
            data-testid={`resultItem-${props.result.id}`}>
            <div aria-owns={`adTitle${props.index}`} />
            <img
                className="h-144 w-full object-cover"
                src={applyImageRule(props.result.imageurl, '/480w/')}
                srcSet={makeSrcSet(props.result.imageurl)}
                alt="Annonsebilde"
                aria-hidden="true"
                data-testid="adImage"
                sizes="(min-width: 990px) 300px, (min-width: 768px) 33vw, (min-width: 480px) 50vw"
                loading={props.imagePreloaded ? undefined : 'lazy'}
            />
            {showTrasactionBadge()}
            <div className="p-16 relative">
                {showUpsellAds()}
                <p className="text-12 text-gray-500 truncate pr-32">{resultItemLocationString(props.result)}</p>
                <h3 className="truncate text-16 font-normal text-gray-800" data-testid="adTitle" id={`adTitle${props.index}`}>
                    <Clickable
                        href={itemUrl}
                        target="_blank"
                        title={props.result.heading}
                        className="text-current hover:no-underline focus:no-underline"
                        onClick={selectItem}
                        data-testid={props.result.upsell && 'upsellClickable'}>
                        {props.result.heading}
                    </Clickable>
                </h3>
                {Heart === undefined ? null : (
                    <div className={cn(styles.favoriteIcon, 'absolute')} data-testid="favoriteIcon" onClick={handleFavouriteClick}>
                        <Heart
                            itemId={Number(props.result.id)}
                            isOpen={showModal}
                            isMobile={Boolean(props.isMobile)}
                            variant="heart"
                            itemType="Ad"
                        />
                    </div>
                )}
                <p className="font-bold my-8">{resultItemPriceString(props.result)}</p>
                <p className="text-14 text-gray-400 mb-0" data-testid="bedroomCapacity">
                    {props.result.no_of_bedrooms} soverom <span className="text-gray-200">•</span> {props.result.sleeping_capacity}{' '}
                    sengeplasser
                </p>
                <p className="text-14 text-gray-400 mt-8 mb-0">
                    {props.result.ad_type !== 'private' ? 'Bedrift' : isTransactionalAd ? 'Privat - Enkel bestilling' : 'Privat'}
                </p>
            </div>
        </Card>
    );
};
